import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Send, Users, AlertTriangle, Eye, Loader2, CheckCircle2, XCircle,
  Clock, Mail, RefreshCw, ChevronRight, Sparkles as SparklesIcon,
  FlaskConical, Upload, FileCode, BookmarkPlus, ChevronDown, Trash2,
} from "lucide-react";
import NewsletterAiWriter from "./NewsletterAiWriter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESEND_DAILY_CAP = 100;

const RECIPIENT_FILTERS = [
  {
    key: "all",
    label: "All opted-in customers",
    helper: "Everyone who checked the marketing box on signup.",
  },
  {
    key: "with_orders",
    label: "Opted-in with at least one order",
    helper: "Skip signups that never converted.",
  },
];

// -----------------------------------------------------------------------------
// Root tab
// -----------------------------------------------------------------------------
export default function NewsletterTab({ token }) {
  const [view, setView] = useState("compose"); // "compose" | "history"

  return (
    <div data-testid="admin-newsletter-panel" className="space-y-6">
      <div className="inline-flex p-1 bg-white border border-charcoal-100 rounded-full">
        <SubTab
          active={view === "compose"} onClick={() => setView("compose")}
          testId="newsletter-tab-compose" icon={Mail} label="Compose"
        />
        <SubTab
          active={view === "history"} onClick={() => setView("history")}
          testId="newsletter-tab-history" icon={Clock} label="History"
        />
      </div>

      {view === "compose" ? (
        <ComposePanel token={token} />
      ) : (
        <HistoryPanel token={token} />
      )}
    </div>
  );
}

const SubTab = ({ active, onClick, testId, icon: Icon, label }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
      active
        ? "bg-[#8B2252] text-white shadow-sm"
        : "text-charcoal-500 hover:text-charcoal-700"
    }`}
  >
    <Icon className="w-3.5 h-3.5" strokeWidth={2} />
    {label}
  </button>
);

// -----------------------------------------------------------------------------
// Compose
// -----------------------------------------------------------------------------
const ComposePanel = ({ token }) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [recipientFilter, setRecipientFilter] = useState("all");
  const [headerSlideId, setHeaderSlideId] = useState("");
  const [bodyIsHtml, setBodyIsHtml] = useState(false);
  const [previewText, setPreviewText] = useState("");
  const [fileLoadedName, setFileLoadedName] = useState("");

  const [recipientInfo, setRecipientInfo] = useState(null); // {count, sample}
  const [slides, setSlides] = useState([]);
  const [previewHtml, setPreviewHtml] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null); // {sent_to} | null
  const [result, setResult] = useState(null); // {sent, failed, failed_emails}
  const [error, setError] = useState("");

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);

  // Fetch slides once for the optional header image dropdown.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/promo-slides`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setSlides(data || []);
      } catch {
        // Non-blocking — header slide is optional.
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Refetch recipient count whenever the filter changes.
  const fetchRecipients = useCallback(async () => {
    try {
      setError("");
      const { data } = await axios.get(
        `${API}/admin/newsletter/recipients?filter=${recipientFilter}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRecipientInfo(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load recipients");
    }
  }, [token, recipientFilter]);
  useEffect(() => { fetchRecipients(); }, [fetchRecipients]);

  const validPayload = useMemo(() => ({
    subject: subject.trim(),
    body: body.trim(),
    recipient_filter: recipientFilter,
    header_slide_id: headerSlideId || null,
    cta_label: ctaLabel.trim() || null,
    cta_url: ctaUrl.trim() || null,
    body_is_html: bodyIsHtml,
    preview_text: previewText.trim(),
  }), [subject, body, recipientFilter, headerSlideId, ctaLabel, ctaUrl, bodyIsHtml, previewText]);

  // ---------------- Templates ----------------
  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/newsletter/templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(data || []);
    } catch {
      // Non-blocking — templates are optional.
    }
  }, [token]);
  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject || "");
    setBody(tpl.body || "");
    setCtaLabel(tpl.cta_label || "");
    setCtaUrl(tpl.cta_url || "");
    setPreviewText(tpl.preview_text || "");
    setBodyIsHtml(!!tpl.body_is_html);
    setFileLoadedName("");
    setTemplatesOpen(false);
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/newsletter/templates/${tpl.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not delete template");
    }
  };

  const saveTemplate = async (name) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/newsletter/templates`,
        {
          name,
          subject: subject.trim(),
          body: body.trim(),
          cta_label: ctaLabel.trim() || null,
          cta_url: ctaUrl.trim() || null,
          body_is_html: bodyIsHtml,
          preview_text: previewText.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setTemplates((prev) => [...prev, data].sort(
        (a, b) => a.name.localeCompare(b.name),
      ));
      setShowSaveTemplate(false);
    } catch (e) {
      throw new Error(e?.response?.data?.detail || "Could not save template");
    }
  };

  // ---------------- File upload ----------------
  const MAX_UPLOAD_BYTES = 500_000; // ~500 KB — plenty for any newsletter body
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(
        `File is too large (${(file.size / 1024).toFixed(0)} KB). ` +
        `Please upload a file under ${MAX_UPLOAD_BYTES / 1000} KB.`,
      );
      e.target.value = "";
      return;
    }
    const isHtml = /\.html?$/i.test(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target?.result || "");
      setBody(text);
      setBodyIsHtml(isHtml);
      setFileLoadedName(file.name);
      setError("");
    };
    reader.onerror = () => {
      setError("Failed to read file.");
    };
    reader.readAsText(file);
    // Reset the input so the same file can be re-selected later.
    e.target.value = "";
  };

  const canSend = validPayload.subject.length > 0
    && validPayload.body.length > 0
    && recipientInfo?.count > 0
    && !sending;

  const openPreview = async () => {
    if (!validPayload.subject || !validPayload.body) return;
    try {
      setError("");
      const { data } = await axios.post(
        `${API}/admin/newsletter/preview`,
        validPayload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPreviewHtml(data.html);
      setShowPreview(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to build preview");
    }
  };

  const sendTest = async () => {
    if (!validPayload.subject || !validPayload.body) return;
    setTestSending(true);
    setTestResult(null);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/admin/newsletter/test-send`,
        validPayload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setTestResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Test send failed");
    } finally {
      setTestSending(false);
    }
  };

  const overCap = (recipientInfo?.count || 0) > RESEND_DAILY_CAP;

  const doSend = async () => {
    setSending(true);
    setError("");
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/newsletter/send`,
        validPayload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult(data);
      // Optional: clear form on success.
      if ((data.sent || 0) > 0 && (data.failed || 0) === 0) {
        setSubject(""); setBody(""); setCtaLabel(""); setCtaUrl("");
        setHeaderSlideId("");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
      setConfirmSend(false);
    }
  };

  return (
    <>
      {error && (
        <div
          data-testid="newsletter-error"
          className="p-3 rounded-2xl bg-red-50 border border-red-100 text-red-700 text-sm"
        >
          {error}
        </div>
      )}

      {result && (
        <ResultCard result={result} onClose={() => setResult(null)} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* LEFT — composer */}
        <div className="md:col-span-2 space-y-5">
          {/* Recipients */}
          <div className="surface-card p-6">
            <div className="overline mb-3">Recipients</div>
            <div className="space-y-2">
              {RECIPIENT_FILTERS.map((opt) => (
                <label
                  key={opt.key}
                  htmlFor={`filter-${opt.key}`}
                  data-testid={`newsletter-filter-${opt.key}`}
                  className={`flex items-start gap-3 rounded-2xl border px-4 py-3 cursor-pointer transition ${
                    recipientFilter === opt.key
                      ? "border-[#8B2252] bg-[#8B2252]/[0.03]"
                      : "border-charcoal-100 hover:border-charcoal-200"
                  }`}
                >
                  <input
                    id={`filter-${opt.key}`}
                    type="radio"
                    name="recipient-filter"
                    checked={recipientFilter === opt.key}
                    onChange={() => setRecipientFilter(opt.key)}
                    className="mt-1 accent-[#8B2252]"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-charcoal-600 font-medium">
                      {opt.label}
                    </div>
                    <div className="text-xs text-charcoal-400 mt-0.5">
                      {opt.helper}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div
              data-testid="newsletter-recipient-count"
              className={`mt-4 rounded-2xl px-4 py-3 border flex items-center gap-3 ${
                overCap
                  ? "bg-amber-50 border-amber-200"
                  : "bg-sage-100/60 border-sage-200"
              }`}
            >
              {overCap
                ? <AlertTriangle className="w-4 h-4 text-amber-700" strokeWidth={2} />
                : <Users className="w-4 h-4 text-sage-700" strokeWidth={2} />}
              <div className="text-sm">
                <div className={overCap ? "text-amber-800 font-semibold" : "text-sage-800 font-semibold"}>
                  {recipientInfo == null
                    ? "Counting…"
                    : `${recipientInfo.count} recipient${recipientInfo.count === 1 ? "" : "s"} will receive this`}
                </div>
                {overCap && (
                  <div className="text-xs text-amber-700 mt-1">
                    Over the Resend free daily cap ({RESEND_DAILY_CAP}/day). Send in batches or upgrade the Resend plan.
                  </div>
                )}
                {recipientInfo?.sample?.length > 0 && (
                  <div className="text-xs text-charcoal-500 mt-1 truncate">
                    e.g. {recipientInfo.sample.slice(0, 3).join(", ")}{recipientInfo.sample.length > 3 ? "…" : ""}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={fetchRecipients}
                className="ml-auto text-xs text-charcoal-500 hover:text-charcoal-700 inline-flex items-center gap-1"
                data-testid="newsletter-refresh-recipients"
              >
                <RefreshCw className="w-3 h-3" />
                Refresh
              </button>
            </div>
          </div>

          {/* Compose */}
          <div className="surface-card p-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="overline">Compose</div>
              <TemplatesPicker
                templates={templates}
                open={templatesOpen}
                setOpen={setTemplatesOpen}
                onPick={applyTemplate}
                onDelete={deleteTemplate}
              />
            </div>

            <NewsletterAiWriter
              token={token}
              currentBody={body}
              onDraft={(d) => {
                if (d.subject) setSubject(d.subject.slice(0, 200));
                if (d.body) { setBody(d.body); setBodyIsHtml(false); setFileLoadedName(""); }
                if (d.ctaLabel) setCtaLabel(d.ctaLabel.slice(0, 60));
                if (d.ctaUrl) setCtaUrl(d.ctaUrl.slice(0, 500));
              }}
            />

            <Field label="Subject line" required>
              <input
                data-testid="newsletter-subject-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. New batch of BPC-157 just landed"
                maxLength={200}
                className="input"
              />
            </Field>

            <Field label="Preview text (shown next to the subject in the inbox)">
              <input
                data-testid="newsletter-preview-text-input"
                value={previewText}
                onChange={(e) => setPreviewText(e.target.value)}
                placeholder="e.g. Spooky season savings on our most-requested compounds"
                maxLength={200}
                className="input"
              />
            </Field>

            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <span className="text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400">
                  Body <span className="ml-1 text-[#8B2252]">*</span>
                  {bodyIsHtml && (
                    <span
                      data-testid="newsletter-body-html-badge"
                      className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#8B2252]/10 border border-[#8B2252]/30 px-2 py-0.5 text-[10px] text-[#8B2252] font-semibold normal-case tracking-normal"
                    >
                      <FileCode className="w-2.5 h-2.5" />
                      HTML mode
                    </span>
                  )}
                </span>
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="newsletter-upload-file"
                    data-testid="newsletter-upload-file-btn"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-charcoal-200 text-charcoal-600 hover:border-charcoal-400 cursor-pointer transition-colors"
                    title="Load body from .html or .txt file"
                  >
                    <Upload className="w-3 h-3" strokeWidth={2} />
                    Upload from file
                  </label>
                  <input
                    id="newsletter-upload-file"
                    data-testid="newsletter-upload-file-input"
                    type="file"
                    accept=".html,.htm,.txt"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  {bodyIsHtml && (
                    <button
                      type="button"
                      onClick={() => { setBodyIsHtml(false); setFileLoadedName(""); }}
                      data-testid="newsletter-clear-html-mode"
                      className="text-[11px] text-charcoal-500 hover:text-charcoal-800 underline"
                      title="Treat body as plain text again"
                    >
                      Back to text
                    </button>
                  )}
                </div>
              </div>
              {fileLoadedName && (
                <div
                  data-testid="newsletter-file-loaded-banner"
                  className="mb-2 flex items-center gap-2 rounded-2xl bg-sage-100/60 border border-sage-200 px-3 py-2 text-xs text-sage-800"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={2} />
                  <span>
                    File loaded: <strong className="font-semibold">{fileLoadedName}</strong>
                    {bodyIsHtml ? " — will render as HTML in the email." : " — loaded as plain text."}
                  </span>
                </div>
              )}
              <textarea
                data-testid="newsletter-body-input"
                value={body}
                onChange={(e) => {
                  setBody(e.target.value);
                  // Manual edits after a file load — clear the "file loaded" banner
                  // but keep bodyIsHtml so the admin can edit their .html safely.
                  if (fileLoadedName) setFileLoadedName("");
                }}
                rows={10}
                maxLength={100000}
                placeholder={bodyIsHtml
                  ? "<h2>Hello researchers</h2>\n<p>Your custom HTML renders here.</p>"
                  : `Hi researcher,\n\nWe've just restocked our Semax and Selank vials. Get 10% off with SEPT10 through Sunday.\n\n— The LuviaRx Team`}
                className="input resize-y min-h-[220px] font-mono text-sm w-full"
              />
              <span className="block text-xs text-charcoal-300 mt-1.5">
                {bodyIsHtml
                  ? "HTML mode active — raw HTML is used verbatim in the email body. {{first_name}} becomes the customer's first name."
                  : "Blank lines create paragraphs. Line breaks are preserved."}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="CTA button label (optional)">
                <input
                  data-testid="newsletter-cta-label-input"
                  value={ctaLabel}
                  onChange={(e) => setCtaLabel(e.target.value)}
                  placeholder="Shop the new batch"
                  maxLength={60}
                  className="input"
                />
              </Field>
              <Field label="CTA button URL (optional)">
                <input
                  data-testid="newsletter-cta-url-input"
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                  placeholder="https://luviarx.com/products"
                  maxLength={500}
                  className="input"
                />
              </Field>
            </div>

            <Field label="Header image (optional)" hint="Feature an existing promo slide at the top of the email.">
              <select
                data-testid="newsletter-header-slide-select"
                value={headerSlideId}
                onChange={(e) => setHeaderSlideId(e.target.value)}
                className="input"
              >
                <option value="">No header banner</option>
                {slides.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.eyebrow_label ? `${s.eyebrow_label} · ` : ""}{s.headline}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </div>

        {/* RIGHT — actions */}
        <div className="space-y-4">
          <div className="surface-card p-5 space-y-3 sticky top-6">
            <div className="text-xs uppercase tracking-[0.18em] text-charcoal-400 font-semibold">
              Send from
            </div>
            <div className="text-sm text-charcoal-700 font-mono flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#8B2252]" strokeWidth={2} />
              orders@luviarx.com
            </div>

            <div className="pt-2 border-t border-charcoal-100 space-y-2">
              <button
                type="button"
                data-testid="newsletter-preview-btn"
                onClick={openPreview}
                disabled={!validPayload.subject || !validPayload.body}
                className="btn-secondary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Eye className="w-4 h-4 mr-2" strokeWidth={2} />
                Preview email
              </button>

              <button
                type="button"
                data-testid="newsletter-test-send-btn"
                onClick={sendTest}
                disabled={!validPayload.subject || !validPayload.body || testSending}
                className="btn-secondary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                title="Sends a single proof email to your admin address"
              >
                {testSending
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending test…</>
                  : <><FlaskConical className="w-4 h-4 mr-2" strokeWidth={2} />Send test to me</>}
              </button>

              {testResult && (
                <div
                  data-testid="newsletter-test-send-result"
                  className="rounded-2xl bg-sage-100/60 border border-sage-200 px-3 py-2 text-xs text-sage-800 flex items-start gap-2"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={2} />
                  <span>
                    Test email sent to <strong>{testResult.sent_to}</strong>. Check your inbox — it&apos;s marked with a &ldquo;TEST EMAIL&rdquo; banner and does not count toward your list.
                  </span>
                </div>
              )}

              <button
                type="button"
                data-testid="newsletter-send-btn"
                onClick={() => setConfirmSend(true)}
                disabled={!canSend}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
                  : <><Send className="w-4 h-4 mr-2" strokeWidth={2} />Send newsletter</>}
              </button>

              <button
                type="button"
                data-testid="newsletter-save-template-btn"
                onClick={() => setShowSaveTemplate(true)}
                disabled={!subject.trim() && !body.trim()}
                className="w-full inline-flex items-center justify-center gap-2 text-xs text-charcoal-500 hover:text-[#8B2252] py-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Save the current subject + body + CTA as a reusable template"
              >
                <BookmarkPlus className="w-3.5 h-3.5" strokeWidth={2} />
                Save as template
              </button>
            </div>

            <div className="pt-2 text-xs text-charcoal-400 leading-relaxed">
              <div className="flex gap-1 items-start">
                <SparklesIcon className="w-3 h-3 mt-0.5 text-[#8B2252] flex-shrink-0" strokeWidth={2} />
                <span>Every email includes a one-click unsubscribe link that instantly flips <code className="text-[10px] bg-charcoal-100 px-1 rounded">marketing_opt_in</code> to false.</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview modal */}
      {showPreview && (
        <Modal onClose={() => setShowPreview(false)} testId="newsletter-preview-modal">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-2xl font-bold text-charcoal-700">
              Email preview
            </h3>
            <button
              type="button"
              onClick={() => setShowPreview(false)}
              className="text-charcoal-400 hover:text-charcoal-700 text-sm"
              data-testid="newsletter-preview-close"
            >
              Close ✕
            </button>
          </div>
          <div className="border border-charcoal-100 rounded-2xl overflow-hidden bg-white">
            <iframe
              data-testid="newsletter-preview-iframe"
              title="Newsletter preview"
              srcDoc={previewHtml}
              sandbox=""
              className="w-full min-h-[600px] border-0"
            />
          </div>
        </Modal>
      )}

      {/* Confirm-send modal */}
      {confirmSend && (
        <Modal onClose={() => setConfirmSend(false)} testId="newsletter-confirm-modal" narrow>
          <h3 className="font-display text-2xl font-bold text-charcoal-700 mb-3">
            {overCap ? "Over the daily cap" : "Send this newsletter?"}
          </h3>
          <p className="text-sm text-charcoal-500 leading-relaxed mb-4">
            You&apos;re about to email
            {" "}<strong className="text-charcoal-700">{recipientInfo?.count || 0} recipients</strong>{" "}
            from <span className="font-mono">orders@luviarx.com</span>.
          </p>

          {overCap && (
            <div
              data-testid="newsletter-cap-warning"
              className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mb-4"
            >
              <div className="flex gap-2 items-start">
                <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" strokeWidth={2} />
                <div className="text-sm text-amber-800">
                  <div className="font-semibold mb-1">
                    Resend free plan cap: {RESEND_DAILY_CAP}/day, 3,000/month.
                  </div>
                  <div>
                    This send has <strong>{recipientInfo?.count}</strong> recipients — over the daily cap. Resend will start returning 429 errors after the first {RESEND_DAILY_CAP} for the day. Consider sending in smaller batches or upgrading Resend before continuing.
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              className="btn-secondary flex-1"
              onClick={() => setConfirmSend(false)}
              data-testid="newsletter-cancel-send"
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary flex-1"
              onClick={doSend}
              disabled={sending}
              data-testid="newsletter-confirm-send"
            >
              {sending
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
                : <><Send className="w-4 h-4 mr-2" strokeWidth={2} />{overCap ? "Send anyway" : "Send now"}</>}
            </button>
          </div>
        </Modal>
      )}

      {/* Save-template modal */}
      {showSaveTemplate && (
        <SaveTemplateModal
          existingNames={templates.map((t) => t.name)}
          onClose={() => setShowSaveTemplate(false)}
          onSave={saveTemplate}
        />
      )}
    </>
  );
};

// -----------------------------------------------------------------------------
// Templates picker (custom dropdown so we can render a delete button per row)
// -----------------------------------------------------------------------------
const TemplatesPicker = ({ templates, open, setOpen, onPick, onDelete }) => (
  <div className="relative">
    <button
      type="button"
      data-testid="newsletter-load-template-btn"
      onClick={() => setOpen(!open)}
      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-charcoal-200 text-charcoal-600 hover:border-charcoal-400 transition-colors"
    >
      <BookmarkPlus className="w-3 h-3" strokeWidth={2} />
      Load template
      <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && (
      <>
        <div
          className="fixed inset-0 z-40"
          onClick={() => setOpen(false)}
          aria-hidden
        />
        <div
          data-testid="newsletter-templates-dropdown"
          className="absolute right-0 mt-2 w-80 max-h-[400px] overflow-y-auto z-50 bg-white rounded-2xl border border-charcoal-100 shadow-xl py-2"
        >
          {templates.length === 0 ? (
            <div className="px-4 py-3 text-xs text-charcoal-400">
              No templates yet. Compose an email, then click <em>Save as template</em>.
            </div>
          ) : (
            templates.map((tpl) => (
              <div
                key={tpl.id}
                data-testid={`newsletter-template-row-${tpl.id}`}
                className="group flex items-start gap-2 px-3 py-2 hover:bg-cream-100/60 transition-colors"
              >
                <button
                  type="button"
                  data-testid={`newsletter-template-pick-${tpl.id}`}
                  onClick={() => onPick(tpl)}
                  className="flex-1 text-left min-w-0"
                >
                  <div className="text-sm text-charcoal-700 font-medium truncate">
                    {tpl.name}
                  </div>
                  <div className="text-[11px] text-charcoal-400 truncate">
                    {tpl.subject || "(no subject)"}
                  </div>
                </button>
                <button
                  type="button"
                  data-testid={`newsletter-template-delete-${tpl.id}`}
                  onClick={(e) => { e.stopPropagation(); onDelete(tpl); }}
                  className="p-1 rounded-full text-charcoal-300 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                  title={`Delete template "${tpl.name}"`}
                  aria-label={`Delete template ${tpl.name}`}
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              </div>
            ))
          )}
        </div>
      </>
    )}
  </div>
);

// -----------------------------------------------------------------------------
// Save-template modal
// -----------------------------------------------------------------------------
const SaveTemplateModal = ({ existingNames, onClose, onSave }) => {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Please give the template a name.");
      return;
    }
    if (existingNames.includes(trimmed)) {
      setError("A template with this name already exists.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      await onSave(trimmed);
    } catch (e) {
      setError(e?.message || "Could not save template");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} testId="newsletter-save-template-modal" narrow>
      <h3 className="font-display text-2xl font-bold text-charcoal-700 mb-3">
        Save as template
      </h3>
      <p className="text-sm text-charcoal-500 leading-relaxed mb-4">
        The current subject, body, and CTA will be saved. You can load it later from the <em>Load template</em> dropdown.
      </p>
      <input
        data-testid="newsletter-save-template-name-input"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleSave()}
        placeholder="e.g. Monthly restock"
        maxLength={80}
        className="input mb-2"
        autoFocus
      />
      {error && (
        <p
          data-testid="newsletter-save-template-error"
          className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-xl px-3 py-2 mb-3"
        >
          {error}
        </p>
      )}
      <div className="flex gap-3 mt-3">
        <button
          type="button"
          className="btn-secondary flex-1"
          onClick={onClose}
          data-testid="newsletter-save-template-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary flex-1"
          onClick={handleSave}
          disabled={saving}
          data-testid="newsletter-save-template-confirm"
        >
          {saving
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
            : <><BookmarkPlus className="w-4 h-4 mr-2" strokeWidth={2} />Save template</>}
        </button>
      </div>
    </Modal>
  );
};

// -----------------------------------------------------------------------------
// Result card (post-send)
// -----------------------------------------------------------------------------
const ResultCard = ({ result, onClose }) => {
  const { sent = 0, failed = 0, failed_emails = [] } = result || {};
  const good = failed === 0;
  return (
    <div
      data-testid="newsletter-send-result"
      className={`rounded-2xl border p-5 ${
        good
          ? "bg-emerald-50 border-emerald-200"
          : "bg-amber-50 border-amber-200"
      }`}
    >
      <div className="flex items-start gap-3">
        {good
          ? <CheckCircle2 className="w-6 h-6 text-emerald-700 flex-shrink-0" strokeWidth={2} />
          : <AlertTriangle className="w-6 h-6 text-amber-700 flex-shrink-0" strokeWidth={2} />}
        <div className="flex-1">
          <div className={good ? "font-semibold text-emerald-800" : "font-semibold text-amber-800"}>
            {good
              ? `Sent to ${sent} recipient${sent === 1 ? "" : "s"}.`
              : `Sent to ${sent}, failed for ${failed}.`}
          </div>
          {failed_emails.length > 0 && (
            <details className="mt-2 text-xs text-charcoal-500">
              <summary className="cursor-pointer">Show failed emails</summary>
              <ul className="mt-1 max-h-40 overflow-y-auto font-mono">
                {failed_emails.map((em) => (
                  <li key={em}>{em}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-charcoal-400 hover:text-charcoal-700 text-sm"
          data-testid="newsletter-result-dismiss"
        >
          Dismiss ✕
        </button>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// History
// -----------------------------------------------------------------------------
const HistoryPanel = ({ token }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  const fetchRows = useCallback(async () => {
    try {
      setError("");
      const { data } = await axios.get(`${API}/admin/newsletter/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load history");
    }
  }, [token]);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  return (
    <div className="surface-card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline mb-1">Sent newsletters</div>
          <h2 className="font-display text-2xl font-bold text-charcoal-700">
            Send history
          </h2>
        </div>
        <button
          type="button"
          onClick={fetchRows}
          data-testid="newsletter-history-refresh"
          className="btn-secondary"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-2xl bg-red-50 text-red-700 text-sm mb-4">
          {error}
        </div>
      )}

      {rows == null ? (
        <div className="text-charcoal-400 text-sm py-8 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div
          data-testid="newsletter-history-empty"
          className="text-charcoal-400 text-sm py-10 text-center"
        >
          No newsletters sent yet. Compose your first one above.
        </div>
      ) : (
        <div data-testid="newsletter-history-list" className="divide-y divide-charcoal-100">
          {rows.map((row) => (
            <HistoryRow key={row.id} row={row} />
          ))}
        </div>
      )}
    </div>
  );
};

const HistoryRow = ({ row }) => {
  const good = row.status === "sent" && (row.failed_count || 0) === 0;
  const partial = (row.failed_count || 0) > 0;
  const when = row.created_at
    ? new Date(row.created_at).toLocaleString(undefined, {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      })
    : "—";
  return (
    <div
      data-testid={`newsletter-history-row-${row.id}`}
      className="py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6"
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-charcoal-700 truncate">
          {row.subject || "(no subject)"}
        </div>
        <div className="text-xs text-charcoal-400 mt-0.5">
          {when} · by {row.sent_by_admin_email || "admin"}
        </div>
      </div>
      <div className="flex items-center gap-4 text-sm">
        <div className="text-charcoal-500">
          <span className="text-charcoal-700 font-semibold">{row.sent_count}</span>
          <span className="text-charcoal-400"> / {row.recipient_count} sent</span>
        </div>
        {partial && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-xs text-amber-800">
            <XCircle className="w-3 h-3" />
            {row.failed_count} failed
          </span>
        )}
        {good && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-xs text-emerald-800">
            <CheckCircle2 className="w-3 h-3" />
            Sent
          </span>
        )}
        {row.status === "sending" && (
          <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 border border-sky-200 px-2.5 py-0.5 text-xs text-sky-800">
            <Loader2 className="w-3 h-3 animate-spin" />
            Sending
          </span>
        )}
        <ChevronRight className="w-4 h-4 text-charcoal-300" />
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Reusables
// -----------------------------------------------------------------------------
const Field = ({ label, hint, required, children }) => (
  <label className="block">
    <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">
      {label}
      {required && <span className="ml-1 text-[#8B2252]">*</span>}
    </span>
    {children}
    {hint && <span className="block text-xs text-charcoal-300 mt-1.5">{hint}</span>}
  </label>
);

const Modal = ({ onClose, testId, narrow, children }) => (
  <div
    data-testid={testId}
    className="fixed inset-0 z-50 bg-charcoal-800/40 backdrop-blur-sm flex items-start justify-center p-4 pt-12 overflow-y-auto"
    onClick={onClose}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className={`bg-white rounded-3xl shadow-xl w-full ${narrow ? "max-w-md" : "max-w-3xl"} p-6 md:p-8`}
    >
      {children}
    </div>
  </div>
);
